import {
  fetchProjectMeeting,
  useSDK,
  type SharedMeetingDetail,
} from '@littlebird/sdk';
import { useSharedProjectDetail } from '@littlebird/ui-utils';
import { useCallback } from 'react';

import { logError } from '@/lib/errorLogging';

import type { JSONContent } from '@tiptap/core';

export interface ProjectSharedMeetingDetail {
  title: string;
  authorEmail: string | null;
  summary: string | null;
  transcript: string | null;
  notes: JSONContent | null;
}

function toDetail(meeting: SharedMeetingDetail): ProjectSharedMeetingDetail {
  return {
    title: meeting.title,
    authorEmail: meeting.authorEmail,
    summary: meeting.summary,
    transcript: meeting.transcript,
    notes: (meeting.notes as JSONContent | null) ?? null,
  };
}

export function useProjectSharedMeetingDetail(
  projectId: string,
  meetingId: string
) {
  const sdk = useSDK();
  const fetchDetail = useCallback(
    () => fetchProjectMeeting(sdk, projectId, meetingId).then(toDetail),
    [meetingId, projectId, sdk]
  );
  const onError = useCallback(
    (error: unknown) => {
      logError(error, 'Failed to load shared project meeting', {
        context: 'useProjectSharedMeetingDetail',
        metadata: { projectId, meetingId },
      });
    },
    [meetingId, projectId]
  );
  return useSharedProjectDetail({
    fetchDetail,
    kind: 'meeting',
    projectId,
    contentId: meetingId,
    onError,
  });
}
